import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { StytchB2B, StytchEventType } from '@stytch/react/b2b';
import { useTranslation } from 'react-i18next';
import { buildB2BPasswordResetConfig } from '@/auth/b2bDiscovery';
import { useRuntimeAuthSdk } from '@/auth/runtimeAuth';
import { FlowCompletingCover } from '@/auth/FlowCompletingCover';
import { AuthBrandLockup } from '@/components/AuthBrandLockup';
import { isDemoMode } from '@/lib/demoMode';
import { goToNextOrHome, validateNextSearch } from '@/lib/nextPath';

const b2bConfig = buildB2BPasswordResetConfig();

function ResetPasswordRoute() {
  const { t } = useTranslation();
  const authSdk = useRuntimeAuthSdk();
  const navigate = useNavigate();
  const { next } = Route.useSearch();
  const token = authSdk.getAuthenticationToken();

  if (!token) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-gradient-to-br from-muted to-muted/80 p-4">
        <div className="text-center">
          <h1 className="mb-2 text-2xl font-bold text-foreground">
            {t('auth.resetPassword.invalidLinkTitle')}
          </h1>
          <p className="mb-4 text-muted-foreground">
            {t('auth.resetPassword.invalidLinkBody')}
          </p>
          <button
            type="button"
            onClick={() => navigate({ to: '/login' })}
            className="text-info underline hover:text-info-strong"
          >
            {t('auth.resetPassword.backToLogin')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <FlowCompletingCover className="relative flex min-h-screen flex-col items-center justify-center bg-gradient-to-br from-muted to-muted/80 p-4">
      <div className="mb-8 text-center">
        <AuthBrandLockup />
        <p className="text-muted-foreground">
          {t('auth.resetPassword.subtitle')}
        </p>
      </div>

      <div className="flex w-full max-w-md justify-center">
        <StytchB2B
          config={b2bConfig}
          callbacks={{
            onEvent: (event) => {
              if (event.type === StytchEventType.AuthenticateFlowComplete) {
                void goToNextOrHome(navigate, next);
              }
            },
          }}
        />
      </div>

      <p className="absolute bottom-4 text-xs text-muted-foreground">
        &copy; {new Date().getFullYear()}{' '}
        <a
          href="https://www.khorium.ai/"
          target="_blank"
          rel="noopener noreferrer"
          className="transition-colors hover:text-foreground"
        >
          Khorium Inc.
        </a>{' '}
        {t('auth.resetPassword.copyright')}
      </p>
    </FlowCompletingCover>
  );
}

export const Route = createFileRoute('/reset-password')({
  validateSearch: validateNextSearch,
  // Demo has no runtime auth provider; bounce home before mounting component.
  beforeLoad: () => {
    if (isDemoMode()) throw redirect({ to: '/' });
  },
  component: ResetPasswordRoute,
});
